import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, MapPin, Phone, Plus, Search, Star, UserPlus, Users } from 'lucide-react';
import { formatMoney, relativeTime, type ChannelType } from '@bakery/shared';

import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  cx,
  useToast,
} from '../components/ui.js';

const CHANNELS: { value: ChannelType; label: string }[] = [
  { value: 'messenger', label: 'Messenger' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'viber', label: 'Viber' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
];

const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map((c) => [c.value, c.label])) as Record<ChannelType, string>;

export function CustomersScreen() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent');
  const [addOpen, setAddOpen] = useState(false);

  const customers = useQuery({
    queryKey: ['customers', search, sort],
    queryFn: () => api.customers.list({ search: search || undefined, sort, limit: 100 }),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold">Clients</h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
            {customers.data?.total ?? 0} on file, with what they buy and where to reach them.
          </p>
        </div>
        <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setAddOpen(true)}>
          New client
        </Button>
      </header>

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, phone, email or @handle…"
            className="pl-9"
          />
        </div>
        <Select value={sort} onChange={(event) => setSort(event.target.value)} className="w-40">
          <option value="recent">Recent</option>
          <option value="name">Name</option>
          <option value="spend">Top spend</option>
        </Select>
      </div>

      {customers.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-[68px]" />
          ))}
        </div>
      ) : customers.data?.customers.length === 0 ? (
        <EmptyState
          icon={<Users className="size-8" />}
          title="No clients yet"
          description="They appear here automatically when you attach someone to a sale, or when a conversation turns into an order."
          action={
            <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setAddOpen(true)}>
              Add the first one
            </Button>
          }
        />
      ) : (
        <ul className="space-y-2">
          {customers.data?.customers.map((customer) => (
            <li key={customer.id}>
              <button
                onClick={() => navigate(`/customers/${customer.id}`)}
                className="card flex w-full items-center gap-4 p-4 text-left transition hover:border-crust-400"
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--color-accent-soft)] font-display text-sm font-semibold text-[var(--color-accent-ink)]">
                  {customer.displayName.slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{customer.displayName}</p>
                  <p className="truncate text-xs text-[var(--color-ink-soft)]">
                    {customer.phone ?? customer.code}
                    {customer.lastOrderAt && ` · last order ${relativeTime(customer.lastOrderAt)}`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="tabular text-sm font-semibold">{formatMoney(customer.totalSpent)}</p>
                  <p className="tabular text-xs text-[var(--color-ink-faint)]">
                    {customer.orderCount} order{customer.orderCount === 1 ? '' : 's'}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {customerId && <CustomerDetail customerId={customerId} onClose={() => navigate('/customers')} />}
      {addOpen && <NewCustomerModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function CustomerDetail({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const profile = useQuery({ queryKey: ['customer', customerId], queryFn: () => api.customers.get(customerId) });
  const orders = useQuery({ queryKey: ['customer-orders', customerId], queryFn: () => api.customers.orders(customerId) });

  if (!profile.data) {
    return (
      <Modal open onClose={onClose} title="Loading…">
        <Skeleton className="h-64" />
      </Modal>
    );
  }

  const customer = profile.data;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={customer.displayName}
      description={`${customer.code} · ${customer.orderCount} orders · ${formatMoney(customer.totalSpent)} lifetime`}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Orders" value={String(customer.orderCount)} />
          <Stat label="Average" value={formatMoney(customer.averageOrderValue)} />
          <Stat label="Open" value={String(customer.openOrderCount)} />
        </div>

        {/* Reach them ---------------------------------------------------- */}
        <section>
          <h3 className="mb-2 text-sm font-semibold">Reach them</h3>
          <div className="flex flex-wrap gap-2">
            {customer.phone && (
              <a
                href={`tel:${customer.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-line-strong)] px-3 py-2 text-sm hover:bg-[var(--color-surface-sunken)]"
              >
                <Phone className="size-3.5" />
                {customer.phone}
              </a>
            )}
            {customer.contactLinks.map((link) => (
              <a
                key={link.channel + link.handle}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-line-strong)] px-3 py-2 text-sm hover:bg-[var(--color-surface-sunken)]"
              >
                <ExternalLink className="size-3.5" />
                {CHANNEL_LABEL[link.channel] ?? link.channel}
              </a>
            ))}
            {customer.contactLinks.length === 0 && !customer.phone && (
              <p className="text-sm text-[var(--color-ink-faint)]">No contact details on file yet.</p>
            )}
          </div>
        </section>

        {customer.addresses.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-semibold">Addresses</h3>
            <ul className="space-y-1.5">
              {customer.addresses.map((address) => (
                <li key={address.id} className="flex items-start gap-2 text-sm">
                  <MapPin className="mt-0.5 size-3.5 shrink-0 text-[var(--color-ink-faint)]" />
                  <span>
                    <span className="font-medium">{address.label}</span> — {address.line1}
                    {address.barangay && `, ${address.barangay}`}, {address.city}
                    {address.landmark && (
                      <span className="block text-xs text-[var(--color-ink-faint)]">Landmark: {address.landmark}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* What they buy ------------------------------------------------- */}
        <div className="grid gap-4 sm:grid-cols-2">
          <section>
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
              <Star className="size-3.5 text-crust-500" /> Buys most
            </h3>
            {customer.topItems.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-faint)]">Nothing yet.</p>
            ) : (
              <ul className="space-y-1">
                {customer.topItems.slice(0, 5).map((item) => (
                  <li key={item.productId} className="flex justify-between text-sm">
                    <span className="min-w-0 truncate">{item.productName}</span>
                    <span className="tabular shrink-0 pl-2 text-[var(--color-ink-soft)]">{item.totalQty}×</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold">Bought recently</h3>
            {customer.recentItems.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-faint)]">Nothing yet.</p>
            ) : (
              <ul className="space-y-1">
                {customer.recentItems.slice(0, 5).map((item) => (
                  <li key={item.productId} className="flex justify-between text-sm">
                    <span className="min-w-0 truncate">{item.productName}</span>
                    <span className="shrink-0 pl-2 text-xs text-[var(--color-ink-faint)]">
                      {relativeTime(item.lastOrderedAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <section>
          <h3 className="mb-2 text-sm font-semibold">Order history</h3>
          <ul className="divide-y divide-[var(--color-line)]">
            {orders.data?.slice(0, 10).map((order) => (
              <li key={order.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="tabular font-medium">{order.orderNo}</span>
                <Badge color={order.status.color}>{order.status.name}</Badge>
                <span className="flex-1 truncate text-xs text-[var(--color-ink-faint)]">
                  {relativeTime(order.createdAt)}
                </span>
                <span className="tabular font-medium">{formatMoney(order.total)}</span>
              </li>
            ))}
            {orders.data?.length === 0 && <li className="py-3 text-sm text-[var(--color-ink-faint)]">No orders yet.</li>}
          </ul>
        </section>
      </div>
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] px-3 py-2.5">
      <p className="text-xs text-[var(--color-ink-soft)]">{label}</p>
      <p className="tabular mt-0.5 font-display text-lg font-semibold">{value}</p>
    </div>
  );
}

function NewCustomerModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    channel: 'messenger' as ChannelType,
    handle: '',
    line1: '',
    barangay: '',
    city: 'Taguig',
  });

  const create = useMutation({
    mutationFn: () =>
      api.customers.create({
        firstName: form.firstName,
        lastName: form.lastName || null,
        phone: form.phone || null,
        email: form.email || null,
        handles: form.handle ? [{ channel: form.channel, handle: form.handle }] : [],
        addresses: form.line1
          ? [{ label: 'Home', line1: form.line1, barangay: form.barangay || null, city: form.city, isDefault: true }]
          : [],
      }),
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.push({ tone: 'ok', title: `${customer.displayName} added`, body: customer.code });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Modal
      open
      onClose={onClose}
      title="New client"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={create.isPending} disabled={!form.firstName} onClick={() => create.mutate()}>
            Save client
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" required>
            <Input value={form.firstName} onChange={set('firstName')} autoFocus />
          </Field>
          <Field label="Last name">
            <Input value={form.lastName} onChange={set('lastName')} />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={set('phone')} placeholder="0917 123 4567" inputMode="tel" />
          </Field>
          <Field label="Email">
            <Input value={form.email} onChange={set('email')} type="email" />
          </Field>
        </div>

        <Field label="Social handle" hint="Lets an incoming message from this person match their record automatically.">
          <div className="flex gap-2">
            <Select value={form.channel} onChange={set('channel')} className="w-36">
              {CHANNELS.map((channel) => (
                <option key={channel.value} value={channel.value}>
                  {channel.label}
                </option>
              ))}
            </Select>
            <Input value={form.handle} onChange={set('handle')} placeholder="@name or number" className="flex-1" />
          </div>
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Address">
            <Input value={form.line1} onChange={set('line1')} placeholder="Street" />
          </Field>
          <Field label="Barangay">
            <Input value={form.barangay} onChange={set('barangay')} />
          </Field>
          <Field label="City">
            <Input value={form.city} onChange={set('city')} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
